import { record } from '@rrweb/record'

/** Copy only presentation assets used outside blocked regions. Replay never fetches original URLs. */
export async function captureFonts(blockSelector: string): Promise<string> {
  const families = new Set<string>()
  const familyNames = (value: string) => value.split(',').map(s => s.trim().replace(/^["']|["']$/g, '').toLowerCase())
  for (const element of Array.from(document.querySelectorAll('*')).slice(0, 20000)) {
    if (element.closest(blockSelector) || !element.getClientRects().length) continue
    for (const pseudo of [null, '::before', '::after']) {
      const style = getComputedStyle(element, pseudo)
      if (pseudo && (!style.content || ['none', 'normal'].includes(style.content))) continue
      familyNames(style.fontFamily).forEach(family => families.add(family))
    }
  }
  const faces: CSSFontFaceRule[] = []
  const visit = (rules: CSSRuleList) => {
    for (const rule of Array.from(rules)) {
      if (rule.type === CSSRule.FONT_FACE_RULE && familyNames((rule as CSSFontFaceRule).style.fontFamily).some(family => families.has(family))) faces.push(rule as CSSFontFaceRule)
      else if ('cssRules' in rule) { try { visit((rule as CSSGroupingRule).cssRules) } catch {} }
    }
  }
  for (const sheet of Array.from(document.styleSheets)) { try { visit(sheet.cssRules) } catch {} }
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 3000)
  let budget = 2 * 1024 * 1024
  const result: string[] = []
  try {
    for (const face of faces.slice(0, 16)) {
      const matches = [...face.style.getPropertyValue('src').matchAll(/url\(\s*["']?([^"')]+)["']?\s*\)/gi)]
      for (const match of matches) {
        try {
          const url = new URL(match[1], face.parentStyleSheet?.href || document.baseURI)
          if (!['http:', 'https:'].includes(url.protocol)) continue
          const response = await fetch(url.href, { credentials: url.origin === location.origin ? 'same-origin' : 'omit', signal: controller.signal })
          if (!response.ok || Number(response.headers.get('content-length')) > budget) continue
          const reader = response.body?.getReader()
          if (!reader) continue
          const chunks: Uint8Array[] = []; let size = 0
          while (true) { const next = await reader.read(); if (next.done) break; size += next.value.byteLength; if (size > budget) { await reader.cancel(); throw new Error('Font too large') }; chunks.push(next.value) }
          const bytes = new Uint8Array(size); let offset = 0
          for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length }
          const signature = String.fromCharCode(...bytes.slice(0, 4))
          const mime = signature === 'wOF2' ? 'font/woff2' : signature === 'wOFF' ? 'font/woff' : signature === 'OTTO' ? 'font/otf' : bytes[0] === 0 && bytes[1] === 1 && bytes[2] === 0 && bytes[3] === 0 ? 'font/ttf' : null
          if (!mime) continue
          let binary = ''; for (let i = 0; i < size; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192))
          const style = document.createElement('span').style
          for (const property of ['font-family', 'font-style', 'font-weight', 'font-stretch', 'unicode-range']) style.setProperty(property, face.style.getPropertyValue(property))
          result.push(`@font-face{${style.cssText}src:url(data:${mime};base64,${btoa(binary)})}`)
          budget -= size
          break
        } catch { /* Unavailable fonts use the recorded CSS fallback. */ }
      }
    }
  } finally { clearTimeout(timeout) }
  return result.join('\n')
}

export function embedImages(root: any, blockSelector: string) {
  if (!root) return
  if (root.attributes?.rr_width !== undefined) return
  if (root.tagName === 'img') {
    const element = record.mirror.getNode(root.id) as HTMLImageElement
    if (element && !element.closest(blockSelector) && element.complete && element.naturalWidth && element.naturalWidth * element.naturalHeight <= 2000000) {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = element.naturalWidth; canvas.height = element.naturalHeight
        canvas.getContext('2d')?.drawImage(element, 0, 0)
        const data = canvas.toDataURL('image/png')
        if (data.length <= 1024 * 1024) root.attributes.rr_dataURL = data
      } catch { /* Tainted canvases remain excluded; never change the live image. */ }
    }
  }
  for (const child of root.childNodes || []) embedImages(child, blockSelector)
}
